import { createBrowserRouter } from 'react-router-dom';

import { ActionCenter } from '../attention/ActionCenter';
import { QaAudit } from '../attention/QaAudit';
import { RequireAuth } from '../auth/RequireAuth';
import { GenerationJob, GenerationJobs } from '../authoring/GenerationJobs';
import { GenerationLedger } from '../authoring/GenerationLedger';
import { NewGeneration } from '../authoring/NewGeneration';
import { ReviewWorkspace } from '../authoring/ReviewWorkspace';
import { SystemPrompts } from '../authoring/SystemPrompts';
import { Curriculum } from '../curriculum/Curriculum';
import { CourseDetail } from '../library/CourseDetail';
import { Courses } from '../library/Courses';
import { Lessons } from '../library/Lessons';
import { ModuleDetail } from '../library/ModuleDetail';
import { Modules } from '../library/Modules';
import { NewCollection } from '../library/NewCollection';
import { PathDetail } from '../library/PathDetail';
import { Paths } from '../library/Paths';
import { ToCollection } from '../library/ToCollection';
import { Onboarding } from '../onboarding/Onboarding';

import { AppShell } from './AppShell';
import { Diagnostics } from './Diagnostics';
import { NotFound } from './NotFound';
import { SignIn } from './SignIn';

/**
 * The route tree the back-office hangs from.
 *
 * One authenticated branch, not one guard per route. `RequireAuth` used to be repeated on
 * every entry — six copies, where a seventh route was a single omission away from
 * rendering unauthenticated. It is a rendering decision either way, since the API refuses
 * an unauthenticated caller regardless, but a guard you can forget is worse than one you
 * cannot.
 *
 * Nesting is also what gives every screen the drawer: `AppShell` renders an `Outlet`, so a
 * route added below it is inside the chrome by default rather than by remembering.
 */
export const router = createBrowserRouter([
  { path: '/sign-in', element: <SignIn /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Diagnostics /> },
      { path: 'action-center', element: <ActionCenter /> },
      { path: 'qa-audit', element: <QaAudit /> },
      { path: 'library/courses', element: <Courses /> },
      { path: 'library/courses/new', element: <NewCollection kind="COURSE" /> },
      { path: 'library/courses/:id', element: <CourseDetail /> },
      { path: 'library/modules', element: <Modules /> },
      { path: 'library/modules/:id', element: <ModuleDetail /> },
      { path: 'library/paths', element: <Paths /> },
      { path: 'library/paths/new', element: <NewCollection kind="PATH" /> },
      { path: 'library/paths/:id', element: <PathDetail /> },
      { path: 'library/lessons', element: <Lessons /> },
      { path: 'curriculum', element: <Curriculum /> },
      { path: 'authoring', element: <GenerationJobs /> },
      { path: 'authoring/new', element: <NewGeneration /> },
      { path: 'authoring/ledger', element: <GenerationLedger /> },
      { path: 'authoring/jobs/:id', element: <GenerationJob /> },
      { path: 'authoring/lessons/:id', element: <ReviewWorkspace /> },
      { path: 'onboarding', element: <Onboarding /> },
      { path: 'system-prompts', element: <SystemPrompts /> },
      // Where publishing used to live — GEN-60. Redirected rather than deleted: a
      // reviewer with the old URL in a tab lands where they meant to.
      {
        path: 'authoring/collections/:id/publish',
        element: <ToCollection />,
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
